import type { InstanceInfo } from '@gitlab-org/core';
import { NOT_AVAILABLE, STATUS } from '../render_helpers';
import type { RuntimeContext } from '../../../runtime_context';
import type { GlabDetection } from '../../../utils/glab_detection';
import { MIN_CREDENTIAL_HELPER_VERSION } from '../../../utils/glab_credential_helper';

function versionSuffix(version: string | null): string {
  return version ? ` (${version})` : '';
}

function renderGlabDoctorLines(detection: GlabDetection): string[] {
  if (detection.spawnReachable) {
    if (detection.spawnTooOld) {
      return [
        `- glab: glab${versionSuffix(detection.spawnVersion)} ${STATUS.fail} too old for credential-helper — needs ≥ ${MIN_CREDENTIAL_HELPER_VERSION}`,
      ];
    }
    return [`- glab: glab${versionSuffix(detection.spawnVersion)} ${STATUS.pass}`];
  }

  if (detection.locations.length === 0) {
    return [`- glab: not found ${STATUS.fail}`];
  }

  return detection.locations.map((loc) => {
    const detail = loc.isSnap
      ? 'snap install, unreachable from Duo CLI subprocess'
      : 'installed but not on the Duo CLI subprocess PATH';
    return `- glab: ${loc.path}${versionSuffix(loc.version)} ${STATUS.fail} ${detail}`;
  });
}

export function renderVersions(
  runtimeContext: RuntimeContext,
  instanceInfo: InstanceInfo | undefined,
  glab: GlabDetection,
): string {
  const { envInfo, cliVersion } = runtimeContext;
  const lines = [
    '## Versions',
    `- CLI: ${cliVersion}`,
    `- Node: ${process.version}`,
    `- OS: ${envInfo.osPlatform} ${envInfo.osVersion} (${process.arch})`,
    `- Distribution: ${envInfo.distribution}`,
    `- Terminal: ${envInfo.terminalName}`,
    `- Kitty protocol: ${envInfo.isKittyProtocolSupported ? 'enabled' : 'disabled'}`,
    `- GitLab instance: ${instanceInfo ? `${instanceInfo.instanceVersion} (${instanceInfo.instanceUrl.toString()})` : NOT_AVAILABLE}`,
    ...renderGlabDoctorLines(glab),
  ];
  return lines.join('\n');
}
